"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { IconRenderer } from "@/components/ui/icon-renderer";
import { getContentIcon } from "@/lib/icons";
import { resolvePublicPageUrl } from "@/lib/api";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/ui/animated-section";

export interface RelatedLinkItem {
 title: string;
 href: string;
 description?: string;
 badge?: string;
 iconKey?: string;
}

interface RelatedLinksProps {
 title?: string;
 links: RelatedLinkItem[];
 variant?: "grid" | "list" | "compact";
}

export function RelatedLinks({ title = "Related", links, variant = "grid" }: RelatedLinksProps) {
 if (links.length === 0) return null;

 if (variant === "compact") {
 return (
 <div>
 {title && <h3 className="text-lg font-semibold text-foreground mb-3">{title}</h3>}
 <div className="space-y-2">
 {links.map((link, i) => (
 <Link key={i} href={resolvePublicPageUrl(link.href)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors group py-1">
 {link.iconKey ? <IconRenderer iconKey={link.iconKey} className="w-3.5 h-3.5 text-primary" emojiClassName="text-sm leading-none" /> : <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-primary transition-colors" />}
 <span>{link.title}</span>
 {link.badge && (
 <span className="ml-auto text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-full">{link.badge}</span>
 )}
 </Link>
 ))}
 </div>
 </div>
 );
 }

 if (variant === "list") {
 return (
 <section className="py-12">
 {title && (
 <AnimatedSection>
  <h2 className="text-2xl font-bold text-foreground mb-6">{title}</h2>
 </AnimatedSection>
 )}
 <StaggerContainer className="space-y-3" staggerDelay={0.06}>
 {links.map((link, i) => {
  const Icon = getContentIcon(link.title);
  return (
  <StaggerItem key={i}>
    <Link href={resolvePublicPageUrl(link.href)} className="flex items-center justify-between p-4 bg-muted/50 rounded-xl hover:bg-primary/5 transition-all duration-300 group border border-border hover:border-primary/20 hover:shadow-sm">
   <div className="flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/15 transition-colors">
    {link.iconKey ? <IconRenderer iconKey={link.iconKey} className="w-4 h-4 text-primary" emojiClassName="text-lg leading-none" /> : <Icon className="w-4 h-4 text-primary" />}
    </div>
    <div>
    <span className="font-medium text-foreground group-hover:text-primary transition-colors">{link.title}</span>
    {link.description && <p className="text-sm text-muted-foreground mt-0.5">{link.description}</p>}
    </div>
   </div>
   <ArrowRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
   </Link>
  </StaggerItem>
  );
 })}
 </StaggerContainer>
 </section>
 );
 }

 // grid variant (default)
 return (
 <section className="py-12">
 {title && (
 <AnimatedSection>
  <h2 className="text-2xl font-bold text-foreground mb-6">{title}</h2>
 </AnimatedSection>
 )}
 <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" staggerDelay={0.06}>
 {links.map((link, i) => {
  const Icon = getContentIcon(link.title);
  return (
  <StaggerItem key={i}>
    <Link href={resolvePublicPageUrl(link.href)}>
   <div className="group relative h-full rounded-xl p-px bg-gradient-to-br from-border via-transparent to-border hover:from-primary/30 hover:via-primary/5 hover:to-primary/30 transition-all duration-500">
    <div className="h-full rounded-[calc(0.75rem-1px)] bg-card p-5">
    <div className="flex items-start gap-3">
     <div className="relative w-10 h-10 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center shrink-0 group-hover:from-primary/25 group-hover:to-primary/10 transition-colors duration-300">
        {link.iconKey ? <IconRenderer iconKey={link.iconKey} className="w-5 h-5 text-primary" emojiClassName="text-xl leading-none" /> : <Icon className="w-5 h-5 text-primary" />}
     </div>
     <div className="min-w-0">
     {link.badge && (
      <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full mb-2 inline-block">{link.badge}</span>
     )}
     <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors duration-300">{link.title}</h3>
     {link.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{link.description}</p>}
     </div>
    </div>
    </div>
   </div>
   </Link>
  </StaggerItem>
  );
 })}
 </StaggerContainer>
 </section>
 );
}
